import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '../ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/shadcn/card';

/**
 * Die globale Fehlergrenze — Auftrag `W-frischebundle`, DRINGEND.
 *
 * **Der Auslöser.** `Uncaught NotFoundError: Failed to execute 'removeChild'
 * on 'Node'` aus `react-dom`, gemessen auf `/belege` am 2026-08-28: eine
 * komplett weisse Seite ohne jede Erklärung. Die Signatur ist bekannt (React-
 * Issue #11538): Eine DOM-mutierende Browser-Erweiterung — oder Chromes
 * eingebauter Übersetzer — hängt eigene Knoten in den von React verwalteten
 * Baum, React versucht später, einen Knoten zu entfernen, den es an dieser
 * Stelle nicht mehr vorfindet, und wirft. **React selbst hat dafür keine
 * Wiederherstellung** — der einzige verlässliche Ausweg ist eine Fehlergrenze,
 * die den Absturz auffängt, bevor der Anwender eine leere Fläche sieht.
 *
 * **Was diese Grenze NICHT tut.** Keine automatische Meldung an einen
 * Endpunkt — das wäre ein neuer öffentlicher Endpunkt, den dieser
 * Dringlichkeitsauftrag ausdrücklich nicht vorsieht. `console.error` reicht:
 * Wer den Fehler untersuchen will, hat die Entwicklerkonsole.
 *
 * **Die Chunk-Selbstheilung sitzt HIER und nicht in einer eigenen
 * Komponente.** Ein Vite-Lazy-Import, der ins Leere greift — Signatur
 * `Failed to fetch dynamically imported module` bzw. `Importing a module
 * script failed`, typischerweise weil ein alter, offen gelassener Tab nach
 * einem Deploy einen pensionierten gehashten Chunk anfordert —, wirft seinen
 * Ablehnungsgrund als ganz gewöhnlichen Renderfehler innerhalb der
 * `Suspense`-Grenze in `App.tsx`. Diese Klasse ist die nächste Fehlergrenze
 * darüber (siehe `main.tsx`) und damit der einzige Ort, an dem beide
 * Fehlerklassen ohnehin schon ankommen.
 *
 * **Schleifenschutz per `sessionStorage`-Marke.** Ein einziger `reload()`
 * behebt den häufigen Fall (frisches `index.html` mit neuen Hashes im
 * selben Tab); ohne Marke reloadete ein WIRKLICH kaputtes Bündel endlos.
 * Die Marke wird gesetzt, bevor der Reload beginnt, und erst dann wieder
 * gelöscht, wenn ein Einhängen **ohne** Fehler gelingt — das prüft
 * `componentDidMount()` unten: Bei einem Fehler während des allerersten
 * Einhängens ist `state.hasError` zu diesem Zeitpunkt bereits wahr (React
 * ruft `getDerivedStateFromError` vor dem committenden Render auf), bei
 * einem sauberen Einhängen bleibt es falsch. Ein zweiter, unabhängiger
 * Chunkfehler später in derselben Sitzung — nach einem erfolgreichen
 * Einhängen — bekommt damit wieder einen eigenen Neuladeversuch.
 */

const CHUNK_RELOAD_MARKE = 'fameacc.chunk-reload';

/**
 * Erkennt die beiden bekannten Fehlertexte fehlgeschlagener dynamischer
 * Importe. Vite/ESM formuliert sie leicht unterschiedlich je nach Browser —
 * beide werden abgedeckt, keiner der beiden Texte wird fest angenommen.
 */
function istChunkFehler(error: unknown): boolean {
  const nachricht = error instanceof Error ? error.message : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(nachricht) ||
    /Importing a module script failed/i.test(nachricht) ||
    /error loading dynamically imported module/i.test(nachricht)
  );
}

/** Liest/schreibt die Marke fehlertolerant — ein privates Fenster ohne Speicher darf den Absturz nicht verdoppeln. */
function chunkReloadSchonVersucht(): boolean {
  try {
    return window.sessionStorage.getItem(CHUNK_RELOAD_MARKE) === '1';
  } catch {
    // Kein Speicher erreichbar: als „schon versucht" behandeln, keine
    // Neuladeschleife riskieren, stattdessen sofort die Fehlerkarte zeigen.
    return true;
  }
}

function chunkReloadMarkeSetzen(): void {
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_MARKE, '1');
  } catch {
    /* kein Speicher, kein Schaden -- der Aufrufer reloadet trotzdem */
  }
}

function chunkReloadMarkeLoeschen(): void {
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_MARKE);
  } catch {
    /* kein Speicher, nichts zu tun */
  }
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
  readonly error: Error | undefined;
}

export class ErrorBoundary extends Component<{ readonly children: ReactNode }, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = { hasError: false, error: undefined };

  public static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error: error instanceof Error ? error : new Error(String(error)) };
  }

  public override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Die Ansicht ist auf einen Fehler gelaufen:', error, info.componentStack);

    if (istChunkFehler(error) && !chunkReloadSchonVersucht()) {
      chunkReloadMarkeSetzen();
      window.location.reload();
    }
  }

  public override componentDidMount(): void {
    // Nur bei einem SAUBEREN Einhängen loeschen -- siehe Kopfkommentar. Bei
    // einem Fehler waehrend des allerersten Renderns steht `hasError` hier
    // bereits, und die Marke bleibt fuer den Retry-Zweig oben stehen.
    if (!this.state.hasError) {
      chunkReloadMarkeLoeschen();
    }
  }

  public override render(): ReactNode {
    if (this.state.hasError) {
      return <Fehlerkarte error={this.state.error} />;
    }
    return this.props.children;
  }
}

function Fehlerkarte(props: { readonly error: Error | undefined }): ReactNode {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Die Ansicht ist auf einen Fehler gelaufen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Bitte laden Sie die Seite neu. Bleibt der Fehler bestehen, wenden Sie sich an die
            Systembetreuung.
          </p>
          <Button
            type="button"
            onClick={() => {
              window.location.reload();
            }}
          >
            Neu laden
          </Button>
          {props.error === undefined ? null : (
            <p className="text-xs text-muted-foreground">{props.error.name}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
